import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useOutletContext } from 'react-router-dom';
import { 
  Search, 
  BookOpen, 
  Eye, 
  Star,
  Users,
  Grid,
  List as ListIcon,
  RefreshCw,
  Plus,
  Filter,
  ArrowUpDown,
  X,
  Image
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import { fetchCuratorCollections } from '../../utils/curatorCollectionsApi';

import '../../styles/CollectionAdmin.css';

const CuratorCollections = () => {
  const navigate = useNavigate();
  const { selectedOrganization } = useOutletContext() || {};
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [featuredFilter, setFeaturedFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('list');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    featured: 0,
    avgArtworks: 0,
    withPublicArt: 0
  });

  // Get current user
  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  };

  // Handle collection detail view - uses admin component but in curator route
  const handleViewCollection = (collectionId) => {
    navigate(`/curator/collections/${collectionId}`);
  };

  // Handle collection card click - uses admin component but in curator route
  const handleCollectionClick = (collectionId) => {
    console.log('CuratorCollections: handleCollectionClick called with:', collectionId);
    console.log('CuratorCollections: typeof collectionId:', typeof collectionId);
    navigate(`/curator/collections/${collectionId}`);
  };

  // Load collections for curator's galleries
  const loadCollections = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const user = await getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Use our direct SQL API with organization filter
      const organizationId = selectedOrganization?.organization_id || null;
      const result = await fetchCuratorCollections(user.id, searchQuery, typeFilter, organizationId);
      
      console.log('CuratorCollections: API result:', result);
      console.log('CuratorCollections: Collections sample:', result.collections?.[0]);
      
      setCollections(result.collections);
      setStats({
        total: result.stats.total || 0,
        featured: result.stats.featured || 0,
        avgArtworks: result.stats.avgArtworks || 0,
        withPublicArt: result.collections.filter(c => c.artwork_count > 0).length
      });
      setTotalCount(result.collections.length);

    } catch (err) {
      console.error('Error loading collections:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('CuratorCollections: selectedOrganization changed:', selectedOrganization?.organization_id);
    loadCollections();
  }, [searchQuery, typeFilter, featuredFilter, sortBy, sortOrder, selectedOrganization?.organization_id]);

  // Handle search with debouncing
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setCurrentPage(1);
  };

  // Filter collections based on search and type
  const filteredCollections = collections.filter(collection => {
    const matchesSearch = !searchQuery || 
      collection.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      collection.creator?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      collection.gallery?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesType = typeFilter === 'all' || 
      (typeFilter === 'featured' && collection.is_featured) ||
      collection.type === typeFilter;

    const matchesFeatured = featuredFilter === 'all' ||
      (featuredFilter === 'featured' && collection.is_featured) ||
      (featuredFilter === 'not_featured' && !collection.is_featured);
    
    return matchesSearch && matchesType && matchesFeatured;
  }).sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];
    
    if (sortBy === 'creator_name') {
      aValue = a.creator?.name || '';
      bValue = b.creator?.name || '';
    }
    
    if (typeof aValue === 'string') {
      return sortOrder === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
  });

  // Helper to add Cloudinary transformation
  const addCloudinaryTransform = (url, transform = 't_fitmax-300/') => {
    if (!url || !url.includes('cloudinary.com')) return url;
    return url.replace('/upload/', `/upload/${transform}`);
  };

  // Collection card component for grid view
  const CollectionCard = ({ collection }) => (
    <div 
      className="collection-card" 
      onClick={() => handleCollectionClick(collection.collection_id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleCollectionClick(collection.collection_id);
        }
      }}
    >
      <div className="collection-thumbnail">
        {collection.cover_image ? (
          <img src={addCloudinaryTransform(collection.cover_image, 't_fitmax-300/')} alt={collection.title} />
        ) : (
          <div className="collection-placeholder">
            <BookOpen className="placeholder-icon" color="#6c757d" />
          </div>
        )}
        {collection.is_featured && (
          <div className="featured-badge">
            <Star size={16} color="#ffc107" />
            Featured
          </div>
        )}
      </div>
      
      <div className="collection-info">
        <h3 className="collection-title">{collection.title}</h3>
        <p className="collection-description">{collection.description || 'No description'}</p>
      </div>
    </div>
  );

  // Collection row component for list view
  const CollectionRow = ({ collection }) => (
    <tr 
      className="collection-row"
      onClick={() => handleCollectionClick(collection.collection_id)}
    >
      <td className="collection-name-cell">
        <div className="collection-name-wrapper">
          <div className="collection-thumb-square">
            {collection.cover_image ? (
              <img src={addCloudinaryTransform(collection.cover_image, 't_square-300/')} alt={collection.title} />
            ) : (
              <div className="collection-thumb-placeholder">
                <BookOpen size={24} />
              </div>
            )}
          </div>
          <div className="collection-name-info">
            <span className="collection-full-name">
              {collection.title}
              {(collection.is_featured || collection.is_currently_featured) && (
                <Star size={12} className="featured-star" />
              )}
            </span>
            <span className="collection-meta-line">
              {collection.type && <span className="collection-type-badge">{collection.type}</span>}
              <span className="collection-artwork-count">
                <Image size={10} /> {collection.artwork_counts?.total || collection.artwork_counts?.public || 0} artworks
              </span>
              {collection.artist_count > 0 && (
                <span className="collection-artist-count">
                  • {collection.artist_count} artist{collection.artist_count !== 1 ? 's' : ''}
                </span>
              )}
            </span>
            {collection.description && (
              <span className="collection-description-line">
                {collection.description.length > 80 
                  ? collection.description.substring(0, 80) + '...' 
                  : collection.description}
              </span>
            )}
          </div>
        </div>
      </td>
    </tr>
  );

  // Pagination component
  const Pagination = () => {
    const totalPages = Math.ceil(totalCount / pageSize);
    
    return (
      <div className="pagination">
        <div className="pagination-info">
          Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} collections
        </div>
        <div className="pagination-controls">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="btn-pagination"
          >
            Previous
          </button>
          <span className="page-indicator">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage >= totalPages}
            className="btn-pagination"
          >
            Next
          </button>
        </div>
      </div>
    );
  };




  if (loading) {
    return (
      <div className="collection-admin">
        <div className="collection-admin-container">
          <div className="loading-state">
            <span className="fas fa-sync-alt loading-spinner"></span>
            <span>Loading collections...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="collection-admin">
        <div className="collection-admin-container">
          <div className="error-banner">
            <span>Error: {error}</span>
            <button onClick={loadCollections} className="retry-btn">
              <span className="fas fa-sync-alt" style={{fontSize: '16px'}}></span>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="collection-admin">
      <div className="collection-admin-container">
        {/* Action buttons - no card wrapper */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <Link 
            to="/curator/collections/new"
            className="btn-primary"
            style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              textDecoration: 'none',
              fontSize: '0.875rem',
              width: 'auto',
              maxWidth: '150px'
            }}
          >
            <Plus size={18} />
            Create
          </Link>
          <button 
            onClick={loadCollections} 
            className="refresh-button-icon"
            title="Refresh collections"
          >
            <RefreshCw size={20} />
          </button>
        </div>
        
        {/* Controls - only show when more than 10 collections */}
        {stats.total > 10 && (
        <div className="contacts-controls">
          <div className="controls-row">
            <div className="search-container">
              <Search size={16} className="search-icon" />
              <input
                type="text"
                placeholder="Search collections..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="search-input"
              />
              {searchQuery && (
                <button 
                  className="search-clear"
                  onClick={() => setSearchQuery('')}
                >
                  <X size={14} />
                </button>
              )}
            </div>
            
            {/* Compact icon-based filters */}
            <div className="icon-filters">
              <div className="filter-dropdown">
                <button 
                  className={`icon-filter-btn ${typeFilter !== 'all' ? 'active' : ''}`}
                  title="Filter by type"
                >
                  <Filter size={16} />
                </button>
                <div className="filter-dropdown-menu">
                  <button className={typeFilter === 'all' ? 'active' : ''} onClick={() => setTypeFilter('all')}>All Types</button>
                  <button className={typeFilter === 'collection' ? 'active' : ''} onClick={() => setTypeFilter('collection')}>Collection</button>
                  <button className={typeFilter === 'show' ? 'active' : ''} onClick={() => setTypeFilter('show')}>Show</button>
                  <button className={typeFilter === 'event' ? 'active' : ''} onClick={() => setTypeFilter('event')}>Event</button>
                </div>
              </div>
              
              <div className="filter-dropdown">
                <button 
                  className={`icon-filter-btn ${featuredFilter !== 'all' ? 'active' : ''}`}
                  title="Filter by featured"
                >
                  <Star size={16} />
                </button>
                <div className="filter-dropdown-menu">
                  <button className={featuredFilter === 'all' ? 'active' : ''} onClick={() => setFeaturedFilter('all')}>All Status</button>
                  <button className={featuredFilter === 'featured' ? 'active' : ''} onClick={() => setFeaturedFilter('featured')}>Featured</button>
                  <button className={featuredFilter === 'not_featured' ? 'active' : ''} onClick={() => setFeaturedFilter('not_featured')}>Not Featured</button>
                </div>
              </div>
              
              <div className="filter-dropdown">
                <button 
                  className="icon-filter-btn"
                  title="Sort by"
                >
                  <ArrowUpDown size={16} />
                </button>
                <div className="filter-dropdown-menu">
                  <button className={sortBy === 'created_date' ? 'active' : ''} onClick={() => setSortBy('created_date')}>Date Created</button>
                  <button className={sortBy === 'title' ? 'active' : ''} onClick={() => setSortBy('title')}>Title</button>
                  <button className={sortBy === 'artwork_count' ? 'active' : ''} onClick={() => setSortBy('artwork_count')}>Artwork Count</button>
                  <button className={sortBy === 'creator_name' ? 'active' : ''} onClick={() => setSortBy('creator_name')}>Creator</button>
                </div>
              </div>
              
              <button
                className="icon-filter-btn"
                onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                title={sortOrder === 'asc' ? 'Sort ascending' : 'Sort descending'}
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
            
            <div className="view-toggle">
              <button
                className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Grid view"
              >
                <Grid size={16} />
              </button>
              <button
                className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title="List view"
              >
                <ListIcon size={16} />
              </button>
            </div>
          </div>
          
          {/* Filter summary line */}
          <div className="filter-summary">
            <span className="filter-summary-text">
              Showing: <strong>{filteredCollections.length}</strong> {filteredCollections.length === 1 ? 'collection' : 'collections'}
              {typeFilter !== 'all' && (
                <span className="filter-tag">
                  {typeFilter}
                  <button onClick={() => setTypeFilter('all')}><X size={10} /></button>
                </span>
              )}
              {featuredFilter !== 'all' && (
                <span className="filter-tag">
                  {featuredFilter === 'featured' ? 'Featured' : 'Not Featured'}
                  <button onClick={() => setFeaturedFilter('all')}><X size={10} /></button>
                </span>
              )}
              {searchQuery && (
                <span className="filter-tag">
                  "{searchQuery}"
                  <button onClick={() => setSearchQuery('')}><X size={10} /></button>
                </span>
              )}
              <span className="sort-info">
                sorted by {sortBy === 'created_date' ? 'date created' : sortBy === 'artwork_count' ? 'artworks' : sortBy === 'creator_name' ? 'creator' : sortBy} {sortOrder === 'asc' ? '↑' : '↓'}
              </span>
            </span>
          </div>
        </div>
        )}
        
        {/* Error Display */}
        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="error-close">×</button>
          </div>
        )}
        
        {/* Collections Grid/List */}
        <div className="collections-section">
          {filteredCollections.length === 0 ? (
            <div className="empty-state">
              <BookOpen className="empty-icon" color="#6c757d" />
              <h3>No collections found</h3>
              <p>Try adjusting your filters or search terms.</p>
            </div>
          ) : (
            <>
              <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
                {stats.total} Collection{stats.total !== 1 ? 's' : ''}
              </div>
              {viewMode === 'grid' ? (
                <div className="collections-container grid">
                  {filteredCollections.map((collection) => (
                    <CollectionCard key={collection.id} collection={collection} />
                  ))}
                </div>
              ) : (
                <div className="collections-table-wrapper">
                  <table className="collections-table">
                    <tbody>
                      {filteredCollections.map((collection) => (
                        <CollectionRow key={collection.id} collection={collection} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {stats.total > 10 && <Pagination />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CuratorCollections;
